import react, {useState, useEffect, useContext, useRef} from 'react';
import { debounce} from 'lodash';
import { useErrorBoundary } from 'react-error-boundary';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Grid2 as Grid, Box, Typography, Stack, FormControl, Select, MenuItem, InputLabel, Button, InputAdornment, OutlinedInput, Paper, IconButton, Alert, useMediaQuery, useTheme, TableContainer, Table, TableHead, TableRow, TableCell, TablePagination, TableFooter} from '@mui/material';

import { PictureAsPdf} from '@mui/icons-material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

import { FilersDataContext } from '../../Filers';
import ReportItemRecordType from '../../../../types/reports/reportItemRecordType';
import DropdownOption from '../../../../types/dropdownOption';
import {ReceiptColumns} from '../../../../types/referenceData/dataTableColumns';

import '../../view-filers.css';
import { ReceiptItem } from '../../../../types/reports/receiptItem';
import ContributionsTable from '../../../Data/components/ContributionsTable';
import { ReceiptSortOptions } from '../../../../types/referenceData/dataTableSortingOptions';


const FilerContributions = (): JSX.Element => {
  
    //pass search from browser
    const queryParams = new URLSearchParams(window.location.search)
    const theme = useTheme();
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));

    const FilerDataContext = useContext(FilersDataContext);
    const currentFiler = FilerDataContext.currentFilerPayload?.filer;
    const { showBoundary } = useErrorBoundary();

    const [itemsSummary, setItemsSummary] = useState('Loading...');
    const [receiptRows, setReceiptRows] = useState<ReceiptItem[]>([]);
    const [sortByOptions, setSortByOptions] = useState<DropdownOption[]>(ReceiptSortOptions);
    const [orderBy, setOrderBy] = useState('date');
    const [order, setOrder] = useState<'desc' | 'asc'>('desc');
    
    const [tableColumns, setColumns] = useState(ReceiptColumns);
    const [tableRows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    const [excelExportUrl, setExcelExportUrl] = useState('');
    const [txtExportUrl, setTxtExportUrl] = useState('');
    const [pdfExportUrl, setPdfExportUrl] = useState('');

    const isFirstLoad = useRef(true);

    const [searchType, setSearchType] = useState(queryParams.get('type') || "A");

    const defaultFilters = {
        searchTypeCategory: searchType,
        startDate: '',
        endDate: '',
        pagesize: 50,
        startIndex: 1,
        sortField: '',
        sortDirection: 'DESC',
        cpfId: currentFiler?.cpfId,
        recordTypeId: "-1",
        name: "",
        cityCode: "-1",
        state: "",
        zipCode: "",
        occupation: "",
        employer: "",
        minAmount: "",
        maxAmount: "",
        description: "",
        withSummary: "true",    

    }

    const [reportItemRecordTypes, setReportItemRecordTypes] = useState<ReportItemRecordType[]>();
    const [cityCodeList, setCityCodeList] = useState<DropdownOption[]>();
    
    const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [filters, setFilters] = useState(defaultFilters);
    const [filterCityCode, setFilterCityCode] = useState(filters.cityCode);
    const [filterState, setFilterState] = useState(filters.state);
    const [filterZipCode, setFilterZipCode] = useState(filters.zipCode);
    const [filterOccupation, setFilterOccupation] = useState(filters.occupation);
    const [filterEmployer, setFilterEmployer] = useState(filters.employer);

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    

     useEffect(() => {
        if(isFirstLoad.current){
            getReportItemRecordList();
            isFirstLoad.current = false
        }
        else{
            getFilerReportItems();
        }
      }, [filters])

      const openFilters = (event: any) => {
        getCityCodeList();
        setIsMoreFiltersOpen(true);
      };
    
      const closeFilters = () => {
        setIsMoreFiltersOpen(false);
      };
    
      //pagination
      const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
        setFilters({...filters, startIndex: (newPage * pageSize + 1)});
      };
    
      //sorting  
      const updateSortDirection = (event: any) => {
        let order = event.target.value;
        if(order){
          setOrder(order);
          setFilters({ ...filters, sortDirection: order });
        }
      }
    
      const updateSortField = (event: any) => {
        let sortField = event.target.value;
        if(event.target.value === "-1"){
          setOrderBy('date');
          sortField = 'date';
        }
        else{
          setOrderBy(event.target.value);
        }
        setFilters({ ...filters, sortField: sortField });
      }
    

      const getReportItemRecordList = async () => {

        try{
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/search/recordTypes/${searchType}`)
          .then(result => result.json())
          .then(result => {
            setReportItemRecordTypes(result);
            getFilerReportItems();
          });
        }catch(error){
          console.error('Uncaught Error:', error);
          showBoundary(new Error(`${error}`));
        }
      }

      const getCityCodeList = async () => {
        try{
          await fetch(`${process.env.REACT_APP_API_BASE_URL}//municipalityList/options`)
          .then(result => result.json())
          .then(result => {
            let list = result.filter((item: DropdownOption) => item.value !== "");
            setCityCodeList(list);
          });
        }catch(error){
          console.error('Uncaught Error:', error);
          showBoundary(new Error(`${error}`));
        }
      }

      const getFilerReportItems = async () => {
        try{
            let params = new URLSearchParams({
                ...filters,
                cpfId: currentFiler?.cpfId?.toString() || '',
                pagesize: filters.pagesize.toString(),
                startIndex: filters.startIndex.toString(),
                withSummary: filters.withSummary.toString(),
                searchTypeCategory: currentFiler?.accountType.code==="V"?"D":"A",
            });
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/search/items?${params.toString()}`)
            .then(result => {
              if (!result.ok) {
                showBoundary(new Error('Failed to get filer data.'));
              }
              return result.json();
            })
            .then(result => {
                let summary = result.summary;
                if (summary.count === 0){
                    setItemsSummary("No data found.");
                    setTotalCount(0);
                    setRows([]);
                    setReceiptRows([]);
                }
                else{
                  let startCount = page*pageSize+1;
                  let endCount = summary.count<50? summary.count : (page+1)*pageSize;
                  setItemsSummary(`Viewing ${startCount} through ${endCount} of ${summary.count} items, ${summary.totalDisplay}`);
                  setTotalCount(summary.count);
                  setRows(result.items);
                  setReceiptRows(result.items);
                }
                setExcelExportUrl(`${process.env.REACT_APP_API_BASE_URL}/search/excelOutput?${params.toString()}`);
                setTxtExportUrl(`${process.env.REACT_APP_API_BASE_URL}/search/textOutput?${params.toString()}`);
                setPdfExportUrl(`${process.env.REACT_APP_API_BASE_URL}/search/pdfOutput?${params.toString()}`);
            });
    
        }catch(error){
          console.error('Uncaught Error:', error);
          showBoundary(new Error(`${error}`));
        }
      }

      const updateItemRecordTypeFilter = (event: any) => {
        setFilters({...filters, recordTypeId: event.target.value});
      }

      const updateSearchPhrase = debounce((event: any) => {
        setFilters({...filters, name: event.target.value});
      }, 1000);

    
      const updateStartDate = debounce((date: Dayjs | null) => {
        if(dayjs(date, 'MM-DD-YYYY', true).isValid() || date === null){
          setFilters({...filters, startDate: date?.toDate().toLocaleDateString() || ''});
        }
        else{
          setFilters({...filters, startDate: ''});
        }
      },1000);

      const updateEndDate = debounce((date: Dayjs | null) => {
        if(dayjs(date, 'MM-DD-YYYY', true).isValid() || date === null){
          setFilters({...filters, endDate: date?.toDate().toLocaleDateString() || ''});
        }
        else{
          setFilters({...filters, endDate: ''});
        }
      }, 1000);

      const updateCityCodeFilter = (event: any) => {
        setFilterCityCode(event.target.value);
        setFilters({...filters, cityCode: event.target.value});
      }

      const updateStateFilter = debounce((event: any) => {
        setFilterState(event.target.value);
        setFilters({...filters, state: event.target.value});
      }, 1000)

      const updateZipFilter = debounce((event: any) => {
        setFilterZipCode(event.target.value);
        setFilters({...filters, zipCode: event.target.value});
      }, 1000)

      const updateOccupationFilter = debounce((event: any) => {
        setFilterOccupation(event.target.value);
        setFilters({...filters, occupation: event.target.value});
      },1000)

      const updateEmployerFilter = debounce((event: any) => {
        setFilterEmployer(event.target.value);
        setFilters({...filters, employer: event.target.value});
      },1000)

      const updateMinAmountFilter = debounce((event: any) => {
        setFilters({...filters, minAmount: event.target.value});
      },1000)

      const updateMaxAmountFilter = debounce((event: any) => {
        setFilters({...filters, maxAmount: event.target.value});
      }, 1000)

    return (
    <Box sx={{ width: '100%' }} marginTop={1}>
      {/*item filter*/}
      <Grid container marginBottom={0}>
        <Grid size={{ xs: 12, md: 11, lg: 10 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1}}>
                <Stack direction="row" spacing={0.5}>
                  <FormControl size="small" sx={{ m: 1, p:0, maxWidth: 200 }}>
                        <OutlinedInput
                            sx={{paddingLeft:1}}
                            placeholder={"Search name..."}
                            onChange={updateSearchPhrase}
                            startAdornment={
                                <InputAdornment position="start">
                                <SearchIcon />
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                </Stack>
                <Stack direction="row" spacing={1}>
                      <FormControl size="small" sx={{ m: 1, p:0, minWidth: 120 }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker label="Start Date"
                              slotProps={
                                { 
                                  textField: { size: 'small' },
                                  field: { clearable: true, onClear: () => updateStartDate(null) } 
                                }
                              }
                              onChange={(newValue) => updateStartDate(newValue)}
                            />
                          </LocalizationProvider>
                      </FormControl>
                      <FormControl size="small" sx={{ m: 1, p:0, minWidth: 120 }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker label="End Date"
                              slotProps={
                                { 
                                  textField: { size: 'small' },
                                  field: { clearable: true, onClear: () => updateEndDate(null) }
                                }
                              }
                              onChange={(newValue) => updateEndDate(newValue)}
                            />
                          </LocalizationProvider>
                      </FormControl>
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  <FormControl size="small" sx={{ m: 1, p:0, minWidth: 80}}>
                    <InputLabel>Minimum Amount</InputLabel>
                    <OutlinedInput
                        label={"Minimum Amount"}
                        onChange={updateMinAmountFilter}
                        />
                  </FormControl>
                  <FormControl size="small" sx={{ m: 1, p:0, minWidth: 80 }}>
                    <InputLabel>Maximum Amount</InputLabel>
                    <OutlinedInput
                        label={"Maximum Amount"}
                        onChange={updateMaxAmountFilter}
                        />
                  </FormControl>
                </Stack>
                {
                   !isMoreFiltersOpen && 
                   <Stack direction="row" spacing={0.5}>
                    <Button 
                        size="small" 
                        variant="outlined"
                        onClick={openFilters}
                        sx={{paddingTop:0, paddingBottom:0, width:120}}
                        startIcon={<FilterListIcon />}>
                            More filters
                    </Button>
                  </Stack>
                }
            </Stack>
        </Grid>
        {
          isMoreFiltersOpen && 
          <Grid size={{ xs: 12, md: 10 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1}}>
                <Stack direction="row" spacing={0.5}>
                  <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <Select 
                      defaultValue={"-1"}
                      onChange={updateItemRecordTypeFilter}
                      >
                      <MenuItem value={"-1"}>All Record Types</MenuItem>
                        {
                            reportItemRecordTypes?.map((type) =>
                            <MenuItem value={type.value} key={type.value} >{type.text}</MenuItem>
                        )
                        }
                    </Select>
                  </FormControl>
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <Select 
                        value={filterCityCode}
                        onChange={updateCityCodeFilter}
                    >
                    <MenuItem value={"-1"}>Choose a MA city or town</MenuItem>
                    {
                        cityCodeList?.map((city) =>
                            <MenuItem 
                                value={city.value} 
                                key={city.value}
                                sx={{fontSize: 'smaller'}}
                            >
                                {city.text}
                            </MenuItem>
                        )
                    }
                    </Select>
                  </FormControl>
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  <FormControl size="small" sx={{ m: 1, p:0, minWidth: 80}}>
                    <InputLabel>State</InputLabel>
                    <OutlinedInput
                        label="State"
                        onChange={updateStateFilter}
                        defaultValue={filterState}
                        />
                  </FormControl>
                  <FormControl size="small" sx={{ m: 1, p:0, minWidth: 120 }}>
                    <InputLabel>Zip Code</InputLabel>
                    <OutlinedInput
                        label="Zip Code"
                        onChange={updateZipFilter}
                        defaultValue={filterZipCode}
                        />
                  </FormControl>
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  <FormControl size="small" sx={{ m: 1, p:0, minWidth: 120 }}>
                  <InputLabel>Occupation</InputLabel>
                    <OutlinedInput
                        label={"Occupation"}
                        onChange={updateOccupationFilter}
                        defaultValue={filterOccupation}
                        />
                  </FormControl>
                  <FormControl size="small" sx={{ m: 1, p:0, minWidth: 120 }}>
                  <InputLabel>Employer</InputLabel>
                    <OutlinedInput
                        label={"Employer"}
                        onChange={updateEmployerFilter}
                        defaultValue={filterEmployer}
                        />
                  </FormControl>
                </Stack>
                <Stack direction="row" spacing={0.5}>
                    <Button 
                        size="small" 
                        variant="outlined"
                        onClick={closeFilters}
                        sx={{paddingTop:0, paddingBottom:0, width:120}}
                        startIcon={<FilterAltOffIcon />}>
                            Fewer filters
                    </Button>
                  </Stack>
              </Stack>
          </Grid>
        }
        <Grid size={{ xs: 12, md: 10 }}>
          <Stack direction={isSM?'column': 'row'} spacing={0.5} justifyContent={isSM ? 'flex-start' : 'space-between'} alignItems={'center'}>
              <Stack>
                  <Typography variant='body2' color='text.secondary' component={'i'}>
                    Contributions with an asterisk (*) are inkind contributions.
                  </Typography>
              </Stack>
              <Stack direction="row" spacing={0.1} alignItems={"center"} flexWrap={"wrap"}>
                  <FormControl sx={{ width: 140 }} size="small">
                      <Select
                        labelId = "data-sort-by"
                        defaultValue={"date"}
                        sx={{ fontSize: 'smaller' }}
                        onChange={updateSortField}
                      >
                        {
                          sortByOptions?.map((option) =>
                            <MenuItem 
                              value={option.value} 
                              key={option.value}
                              sx={{ fontSize: 'smaller' }}
                              >
                                {option.text}
                            </MenuItem>
                          )
                        }
                      </Select>
                  </FormControl>
                  <FormControl sx={{ width: 140 }} size="small">
                      <Select
                        labelId = "data-sort-order"
                        defaultValue={"desc"}
                        sx={{ fontSize: 'smaller' }}
                        onChange={updateSortDirection}
                      >
                          <MenuItem 
                            value={'desc'} 
                            sx={{ fontSize: 'smaller' }}
                            >
                              <Stack direction="row" spacing={0.1} alignItems={"center"}>
                                <Typography variant='body2'>Descending</Typography>
                                <ArrowDownwardIcon color={'action'} fontSize='small'  />
                              </Stack>
                          </MenuItem>
                          <MenuItem 
                            value={'asc'} 
                            sx={{ fontSize: 'smaller' }}
                            >
                              <Stack direction="row" spacing={0.1} alignItems={"center"}>
                                <Typography variant='body2'>Ascending</Typography>
                                <ArrowUpwardIcon color={'action'} fontSize='small'  />
                              </Stack>
                          </MenuItem>
                      </Select>
                  </FormControl>
                  <Box>
                    <Button size="small" variant='text'>Export:</Button>
                    <IconButton aria-label="export to excel" size="small" color="success" onClick={() => window.open(excelExportUrl)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="1.2em" height="1.2em" viewBox="0 0 15 15"><path fill="currentColor" d="M4.5 6.995H4v1h.5zm6 1h.5v-1h-.5zm-6 2.505H4v.5h.5zm6 0v.5h.5v-.5zm-6-6.503H4v1h.5zm6 1h.5v-1h-.5zm3-1.497h.5v-.207l-.146-.147zm-3-3l.354-.354L10.707 0H10.5zm-6 7.495h6v-1h-6zM4.5 11h6v-1h-6zm0-6.003h6v-1h-6zm8 9.003h-10v1h10zM2 13.5v-12H1v12zm11-10v10h1v-10zM2.5 1h8V0h-8zm7.646-.146l3 3l.708-.708l-3-3zM2.5 14a.5.5 0 0 1-.5-.5H1A1.5 1.5 0 0 0 2.5 15zm10 1a1.5 1.5 0 0 0 1.5-1.5h-1a.5.5 0 0 1-.5.5zM2 1.5a.5.5 0 0 1 .5-.5V0A1.5 1.5 0 0 0 1 1.5zm2 3v6h1v-6zm3 0v6h1v-6zm3 0v6h1v-6z"/></svg>
                    </IconButton>
                    <IconButton aria-label="export to txt file" size="small" color="primary" onClick={() => window.open(txtExportUrl)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="1.2em" height="1.2em" viewBox="0 0 16 16"><path fill="currentColor" fillRule="evenodd" d="M2 1.5a.5.5 0 0 1 .5-.5H9a.5.5 0 0 1 .354.146l2.5 2.5A.5.5 0 0 1 12 4v2h-1V5H8.5a.5.5 0 0 1-.5-.5V2H3v12h8v-.5h1v1a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5zm7 .707V4h1.793zM8.875 9.5c-.298.4-.593.803-.827 1.167a5.5 5.5 0 0 0-.374.664c-.091.2-.174.436-.174.669h1a.8.8 0 0 1 .084-.253q.102-.225.305-.54c.173-.267.383-.561.611-.87c.228.309.438.603.61.87q.203.315.306.54a1.3 1.3 0 0 1 .082.233l.002.015v.004l1 .001c0-.233-.083-.469-.174-.669a5.5 5.5 0 0 0-.374-.664a21 21 0 0 0-.827-1.167c.298-.4.593-.803.827-1.167c.147-.228.278-.454.374-.664c.091-.2.174-.435.174-.669h-1v.005l-.002.015l-.013.053a1.3 1.3 0 0 1-.069.18q-.102.225-.305.54a17 17 0 0 1-.611.87a17 17 0 0 1-.61-.87a4.5 4.5 0 0 1-.306-.54A.8.8 0 0 1 8.5 7h-1c0 .234.083.469.174.669c.096.21.227.436.374.664c.234.364.529.767.827 1.167M4 7h3v1H6v4H5V8H4zm8 0h3v1h-1v4h-1V8h-1z" clipRule="evenodd"/></svg>
                    </IconButton>
                    <IconButton aria-label="export to pdf" size="small" onClick={() => window.open(pdfExportUrl)}>
                      <PictureAsPdf/>      
                    </IconButton>   
                  </Box>       
              </Stack>
          </Stack>
        </Grid>
      </Grid>
      <Grid size={12} marginBottom={1} marginTop={1}>
        <Alert icon={false} severity="info"
            sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                maxWidth: isSM?'95%':'90%',
                '.MuiAlert-message': { 
                padding: '4px',
                } 
            }}
        >
            {itemsSummary}
        </Alert>
      </Grid>
      {/*data table*/}
      <Grid size={{ xs: 12, md: 10 }} marginTop={1} marginBottom={1}>
         <TableContainer sx={{ width: isSM?'100%': '90%'}} component={Paper}>
             <Table aria-label="receipts table" size="small">
                 <TableHead>
                     <TableRow>
                        <TableCell>
                            <Typography
                              variant={'subtitle2'}
                              fontWeight={600}
                            >
                              Date
                            </Typography>
                         </TableCell>
                         <TableCell>
                             <Typography
                                variant={'subtitle2'}
                                fontWeight={600}
                              >
                              Contributor
                            </Typography>
                         </TableCell>
                         {
                            isSM &&
                            <TableCell align='right'>
                                <Typography
                                    variant={'subtitle2'}
                                    fontWeight={600}
                                    >
                                    Amount
                                </Typography>
                            </TableCell>
                         }
                         <TableCell>
                          <Typography variant={'subtitle2'} fontWeight={600}>
                            City
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant={'subtitle2'} fontWeight={600}>
                            State
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant={'subtitle2'} fontWeight={600}>
                            Zip
                          </Typography>
                        </TableCell>                     
                         <TableCell>
                             <Typography
                             variant={'subtitle2'}
                             fontWeight={600}>
                                Occupation/Employer
                             </Typography>
                         </TableCell>
                         {
                            !isSM &&
                            <TableCell align='right'>
                                <Typography
                                    variant={'subtitle2'}
                                    fontWeight={600}
                                    >
                                     Amount
                                </Typography>
                            </TableCell>
                         }
                         <TableCell>
                             <Typography
                             variant={'subtitle2'}
                             fontWeight={600}>
                                Source
                             </Typography>
                         </TableCell>
                     </TableRow>
                 </TableHead>
                 <ContributionsTable tableRows={receiptRows} displayFiler={false}></ContributionsTable>
                 <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[50]}
                        count={totalCount}
                        rowsPerPage={50}
                        page={page}
                        showFirstButton
                        showLastButton
                        onPageChange={handleChangePage}
                      />
                    </TableRow>
                 </TableFooter>
             </Table>
         </TableContainer>
      </Grid>
      {/* More filters */}
      {
      /*
      
        <Box 
        sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        maxWidth: isSM? 'auto':'90%',
        '.MuiDataGrid-columnHeaderTitle': { 
            fontWeight: 'bold !important',
        },
        }}>
        <DataGrid 
            rows={tableRows}
            getRowHeight={() => 'auto'}  
            columns={tableColumns}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            paginationMode="server"
            rowCount={totalCount}
            pageSizeOptions={[50]}
            columnVisibilityModel={{
              filerFullNameReverse: false,
            }}
            sortModel={sortModel}
            sortingMode="server"
            onSortModelChange={handleSortModelChange} 
            disableColumnMenu
            sx={{ 
              '.MuiDataGrid-cell': {
                alignItems: 'center',
                whiteSpace: 'normal', 
                wordBreak: 'break-word',
                padding: '3px!important',
              },
            }}
        />
      </Box>

      <Popover
        elevation={1}
        open={false}
        anchorEl={anchorEl}
        onClose={closeFilters}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
       <Paper sx={{ p: 2 }}>
       <Grid marginTop={1}>
          <Grid size={12}>
            <Typography
              variant={'subtitle2'}
              marginBottom={0.5}
              fontWeight={600}
            >
              Record Type
            </Typography>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                  <Select 
                      sx={{fontSize: 'smaller'}} 
                      defaultValue={"-1"}
                      onChange={updateItemRecordTypeFilter}
                      >
                      <MenuItem value={"-1"}>All Record Types</MenuItem>
                        {
                            reportItemRecordTypes?.map((type) =>
                            <MenuItem value={type.value} key={type.value} >{type.text}</MenuItem>
                        )
                        }
                  </Select>
              </FormControl>
          </Grid>
          <Grid size={12}>
            <Typography
              variant={'subtitle2'}
              marginBottom={0.5}
              fontWeight={600}
            >
              From a Massachusetts City:
            </Typography>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <Select 
                        sx={{fontSize: 'smaller'}} 
                        value={filterCityCode}
                        onChange={updateCityCodeFilter}
                    >
                    <MenuItem value={"-1"}>Choose a MA city or town</MenuItem>
                    {
                        cityCodeList?.map((city) =>
                            <MenuItem 
                                value={city.value} 
                                key={city.value}
                                sx={{fontSize: 'smaller'}}
                            >
                                {city.text}
                            </MenuItem>
                        )
                    }
                    </Select>
                </FormControl>
          </Grid>
          <Grid size={12} marginTop={1}>
            <Typography
              variant={'subtitle2'}
              marginBottom={1}
              fontWeight={600}
            >
              From Another State:
            </Typography>
            <Stack direction={"row"} spacing={1}>
                <FormControl size="small" sx={{ m: 1, p:0, minWidth: 80}}>
                    <InputLabel sx={{fontSize: "smaller"}}>State</InputLabel>
                    <OutlinedInput
                        label="State"
                        onChange={updateStateFilter}
                        sx={{ fontSize: 'smaller' }}
                        value={filterState}
                        />
                </FormControl>
                <FormControl size="small" sx={{ m: 1, p:0, minWidth: 120 }}>
                <InputLabel sx={{fontSize: "smaller"}}>Zip Code</InputLabel>
                    <OutlinedInput
                        label="Zip Code"
                        onChange={updateZipFilter}
                        sx={{ fontSize: 'smaller' }}
                        value={filterZipCode}
                        />
                </FormControl>
            </Stack>
          </Grid>
          <Grid size={12} marginTop={1}>
            <Typography
              variant={'subtitle2'}
              marginBottom={1}
              fontWeight={600}
            >
              Contributor Info
            </Typography>
            <Stack direction={"row"} spacing={1}>
                <FormControl size="small" sx={{ m: 1, p:0, minWidth: 120 }}>
                <InputLabel sx={{fontSize: "smaller"}}>Occupation</InputLabel>
                    <OutlinedInput
                        label={"Occupation"}
                        onChange={updateOccupationFilter}
                        sx={{ fontSize: 'smaller' }}
                        value={filterOccupation}
                        />
                </FormControl>
                <FormControl size="small" sx={{ m: 1, p:0, minWidth: 120 }}>
                <InputLabel sx={{fontSize: "smaller"}}>Employer</InputLabel>
                    <OutlinedInput
                        label={"Employer"}
                        onChange={updateEmployerFilter}
                        sx={{ fontSize: 'smaller' }}
                        value={filterEmployer}
                        />
                </FormControl>
            </Stack>
          </Grid>
          <Grid size={12} marginTop={2}>
            <Divider />
          </Grid>
          <Grid container size={12}>
            <Stack
                direction={'row'}
                spacing={2}
                padding={2}
                sx={{
                    justifyContent: "flex-end",
                    alignItems: "center",
              }}
            >
                <Button 
                    size="small" 
                    onClick={closeFilters}
                    variant="text"
                    color="warning"
                    sx={{paddingTop:0.5, paddingBottom:0.5}}
                   >
                        Close
                </Button>
                <Button 
                    size="small" 
                    onClick={closeFilters}
                    color="success"
                    variant="contained"
                    sx={{paddingTop:0.5, paddingBottom:0.5}}
                   >
                        Apply
                </Button>
                <Button 
                    size="small" 
                    onClick={clearAllFilters}
                    color ="primary"
                    variant="contained"
                    sx={{paddingTop:0.5, paddingBottom:0.5}}
                   >
                        Clear Filters
                </Button>
            </Stack>
          </Grid>
        </Grid>
        </Paper>
      </Popover>
      
      */}
      
    </Box>
  );
  };

  export default FilerContributions;